import { useGetCover } from "@/services/cover";
import { VideoGame } from "@/interfaces/api";
import type {
  ChallengeObj,
  Challenge,
  GameState,
  Solution,
} from "@/interfaces/vidyaGrid";
import VidyaGridGame from "@/lib/VidyaGridGame";
import {
  loggifyChallenge,
  makeChallengeKey,
  validatorLiterals,
} from "@/lib/utils";
import { useEffect, useMemo, useState } from "react";
import clientLogger from "@/lib/ClientLogger";

export const useVidyaGrid = () => {
  const startingLives = 12;

  const [ready, setReady] = useState(false);
  const [game, setGame] = useState<VidyaGridGame>(new VidyaGridGame());
  const [tries, setTries] = useState(startingLives);
  const [solutions, setSolutions] = useState<Record<string, Solution>>();
  const [gameState, setGameState] = useState<GameState>("playing");
  const [guessing, setGuessing] = useState(false);
  const [gotWrong, setGotWrong] = useState(false);
  const [challenges, setChallenges] = useState<{
    colCha: ChallengeObj;
    rowCha: ChallengeObj;
  }>({
    colCha: { type: "" as Challenge, label: "" },
    rowCha: { type: "" as Challenge, label: "" },
  });

  const { fetchCover } = useGetCover();

  useEffect(() => {
    setReady(true);
  }, []);

  useEffect(() => {
    if (tries > 0) {
      setGotWrong(true);
      setTimeout(() => {
        setGotWrong(false);
      }, 3000);
      return;
    }

    setGuessing(false);
    setGameState("lost");
  }, [tries]);

  useEffect(() => {
    setGuessing(false);
    if (Object.keys(solutions || {}).length >= 9) setGameState("won");
  }, [solutions]);

  const dialogLabel = useMemo(
    () => `${challenges.colCha.label} X ${challenges.rowCha.label}`,
    [challenges]
  );

  const getSolution = (colCha: ChallengeObj, rowCha: ChallengeObj) =>
    solutions ? solutions[makeChallengeKey(colCha, rowCha)] : undefined;

  const registerSolution = (
    colCha: ChallengeObj,
    rowCha: ChallengeObj,
    solution: Solution
  ) => {
    setSolutions({
      ...solutions,
      [makeChallengeKey(colCha, rowCha)]: solution,
    });
  };

  const handleGuess = (guess: VideoGame) => {
    let correctGuess = false;
    const { colCha, rowCha } = challenges;
    try {
      correctGuess = validatorLiterals[makeChallengeKey(colCha, rowCha)](
        guess,
        colCha,
        rowCha
      );
    } catch {
      correctGuess = false;
    }

    if (!correctGuess) setTries(tries - 1);
    else {
      const solution: Solution = {
        name: guess.name,
        url: "https://images.igdb.com/igdb/image/upload/t_cover_big/nocover.png",
      };

      fetchCover(guess.id)
        .then((cover) => {
          solution.url = `https://images.igdb.com/igdb/image/upload/t_cover_big/${cover.image_id}.jpg`;
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          registerSolution(colCha, rowCha, solution);
        });
    }
  };

  const attemptGuess = (colCha: ChallengeObj, rowCha: ChallengeObj) => {
    setGuessing(true);
    setChallenges({ colCha, rowCha });
  };

  const resetGame = () => {
    if (gameState === "playing") {
      clientLogger.info({
        origin: "reset game",
        message: "Game reset while playing",
        extra: {
          colCha: loggifyChallenge(game.colChallenges),
          rowCha: loggifyChallenge(game.rowChallenges),
        },
      });
    }

    if (gameState === "lost") {
      clientLogger.info({
        origin: "reset game",
        message: "Game reset on loss",
        extra: {
          colCha: loggifyChallenge(game.colChallenges),
          rowCha: loggifyChallenge(game.rowChallenges),
        },
      });
    }

    setGame(new VidyaGridGame());
    setTries(startingLives);
    setSolutions({});
    setGameState("playing");
    setChallenges({
      colCha: { type: "" as Challenge, label: "" },
      rowCha: { type: "" as Challenge, label: "" },
    });
    setGuessing(false);
    setGotWrong(false);
  };

  return {
    colChallenges: game.colChallenges,
    rowChallenges: game.rowChallenges,
    guessing,
    setGuessing,
    ready,
    tries,
    gotWrong,
    handleGuess,
    getSolution,
    dialogLabel,
    gameState,
    attemptGuess,
    resetGame,
    startingLives,
  };
};

export default useVidyaGrid;
